import { AppRoute, AppRoutes, appRoutes } from '@config/app-routes.config';
import { InjectionToken, Provider } from '@angular/core';
import { User } from '@modules/user/models/user.model';

export interface SideBarItem {
  divide?: boolean;
  name?: string;
  icon?: string;
  navigateTo?: AppRoute;
  items?: SideBarItem[];
  isHidden?: boolean;
}

export interface SidebarConfigContract {
  isOpenSidebar: boolean;
  visiblePayment?: boolean;

  dashboard: SideBarItem;
  payment: SideBarItem;
  job: SideBarItem;
  companies: SideBarItem;
  team: SideBarItem;
  customers: SideBarItem;
  registerCompany: SideBarItem;
  transferCompany: SideBarItem;
  overview: SideBarItem;
  incorporate: SideBarItem;

  menuSection: SideBarItem[];

  updateByUser(activeUser: User, visiblePayment?: boolean): SidebarConfigContract;
}

export class SidebarConfig implements SidebarConfigContract {
  isOpenSidebar = true;
  visiblePayment = false;

  dashboard: SideBarItem;
  payment: SideBarItem;
  job: SideBarItem;
  companies: SideBarItem;
  team: SideBarItem;
  customers: SideBarItem;
  registerCompany: SideBarItem;
  transferCompany: SideBarItem;
  incorporate: SideBarItem;
  overview: SideBarItem;
  menuSection: SideBarItem[] = [];

  constructor(protected readonly appRoutes: AppRoutes) {
    this.dashboard = {
      icon: 'space_dashboard',
      name: 'Dashboard',
      navigateTo: appRoutes.dashboard,
    };

    this.payment = {
      icon: 'payment',
      name: 'Payments',
      navigateTo: appRoutes.payments,
    };

    this.job = {
      icon: 'work',
      name: 'Job Request',
      navigateTo: appRoutes.jobs,
    };

    this.companies = {
      icon: 'business',
      name: 'Companies',
      navigateTo: appRoutes.companies,
    };

    this.team = {
      icon: 'supervisor_account',
      name: 'Team',
      navigateTo: appRoutes.team,
    };

    this.customers = {
      icon: 'groups',
      name: 'Customers',
      navigateTo: appRoutes.customers,
    };

    this.registerCompany = {
      icon: 'domain_add',
      name: 'Register Company',
      navigateTo: appRoutes.registerCompany,
    };

    this.transferCompany = {
      icon: 'business',
      name: 'Transfer Company',
      navigateTo: appRoutes.transferCompany,
    };

    this.incorporate = { name: 'Incorporate', items: [] };
    this.overview = { name: 'Overview', items: [] };

    this.setUp();
  }

  private setUp(): void {
    this.incorporate.items = [this.registerCompany, this.transferCompany];
    this.overview.items = [
      this.dashboard,
      this.payment,
      this.job,
      this.companies,
      this.team,
      this.customers,
    ];
    this.menuSection = [this.overview, this.incorporate];
  }

  updateByUser(activeUser: User, visiblePayment = false): SidebarConfig {
    this.visiblePayment = visiblePayment;

    if (!activeUser) {
      this.menuSection = this.menuSection.map(section => ({
        ...section,
        isHidden: true,
      }));
      return this;
    }

    // Hidden rules
    this.team.isHidden = activeUser.isExpert() || activeUser.isCustomer();
    this.customers.isHidden = activeUser.isCustomer();
    this.payment.isHidden = activeUser.isExpert() || !this.visiblePayment;

    // Labels
    this.job.name = activeUser.isCustomer() ? 'My Request' : 'Job Request';
    this.payment.name = activeUser.isCustomer() ? 'My Payments' : 'Payments';
    this.companies.name = activeUser.isCustomer() ? 'My Companies' : 'Companies';

    // Divider
    this.overview.divide = activeUser.isExpert() || activeUser.isCustomer();

    this.setUp();
    return this;
  }
}

export const sideBarConfig = new SidebarConfig(appRoutes);

export const APP_SIDEBAR = new InjectionToken<SidebarConfig>('APP_SIDEBAR');

export const APP_SIDEBAR_CONFIG: Provider = {
  provide: APP_SIDEBAR,
  useValue: sideBarConfig,
};
